const _ = require('lodash');
const math = require('mathjs');
const { assert } = require('../common/assert');
const { PrismoidSeriesContainer } = require('./prismoid-series-container');
const { XYCircle } = require('./shapes');

// a bin container defined by a series of level circles
// Co-ordinate system for this class: [0, 0, 0] is the center of the bottom plane of the bounding box for this container
// Typically this represents the center of the bottom of the hopper
class CircularContainer extends PrismoidSeriesContainer {
  #circles;

  constructor(circles) {
    assert(circles.length > 1, 'At least two circles must be provided');
    assert(circles.every(circle => circle instanceof XYCircle), 'All elements must be instances of XYCircle');
    super(circles);
    this.#circles = circles;
  }

  shapeToSurfacePoints(circle, numSegmentsInCylinder = 60) {
    return _.range(numSegmentsInCylinder).map(idx => ({
      x: circle.cx + circle.r * math.cos((idx * 2 * math.pi) / numSegmentsInCylinder),
      y: circle.cy + circle.r * math.sin((idx * 2 * math.pi) / numSegmentsInCylinder),
      z: circle.z,
    }));
  }

  interpolateZAtPoint(c1, c2, x, y) {
    assert(c2.fullyContains(c1), 'c2 must fully contain c1');
    assert(!c1.containsPoint(x, y), 'c1 must not contain the point');
    assert(c2.containsPoint(x, y), 'c2 must contain the point');

    // we assume that there is a linear progression in z, centrepoint and radius between the two circles. First, we need
    // to figure out how far along that progression we are, given just an x and y coordinate. Then, we can define the
    // circle at that position
    //
    // This solution comes from wolfram alpha solving for R (and therefore f):
    // f = linear progression between 0, 1,
    // cx, cy = centrepoint of interpolated circle,
    // px, py = vertical line we're trying to find intersection of
    // R = radius of interpolated circle
    //
    // f = (cx - x1) / (x2 - x1)
    // f = (R - r1) / (r2 - r1)
    // f = (cy - y1) / (y2 - y1)
    //
    // and
    // R^2 = (px - cx)^2 + (py - cy)^2
    const deltaYToDeltaR = (c2.cy - c1.cy) / (c2.r - c1.r);
    const deltaXToDeltaR = (c2.cx - c1.cx) / (c2.r - c1.r);
    let solutionA;
    let solutionB;
    if (basicallyZero(deltaYToDeltaR ** 2 + deltaXToDeltaR ** 2 - 1)) {
      // Special case where there is a completely vertical line between the two circles
      const sqrtTerm = Math.sqrt(1 - deltaXToDeltaR ** 2);
      const solveOnce = (sqrtTerm) => (
        y ** 2
        - 2 * y * (c1.cy + c1.r * sqrtTerm)
        + c1.r ** 2
        + 2 * c1.r * ((x * deltaXToDeltaR) + c1.cy * sqrtTerm - deltaXToDeltaR * c1.cx)
        + c1.cy ** 2
        + x ** 2
        - 2 * x * c1.cx
        + c1.cx ** 2
      ) / (
        2 * (y * (-sqrtTerm) + c1.r + c1.cy * sqrtTerm + x * deltaXToDeltaR - deltaXToDeltaR * c1.cx)
      );
      solutionA = solveOnce(sqrtTerm);
      solutionB = solveOnce(-sqrtTerm);
    } else {
      const sqrtTerm = Math.sqrt(
        y ** 2 * (-(deltaXToDeltaR ** 2 - 1))
          + 2 * y * (deltaYToDeltaR * (c1.r + x * deltaXToDeltaR - deltaXToDeltaR * c1.cx) + c1.cy * (deltaXToDeltaR ** 2 - 1))
          + deltaYToDeltaR ** 2 * (c1.r ** 2 - (x - c1.cx) ** 2)
          - 2 * deltaYToDeltaR * c1.cy * (c1.r + deltaXToDeltaR * (x - c1.cx))
          + c1.r ** 2 * deltaXToDeltaR ** 2
          + 2 * c1.r * x * deltaXToDeltaR
          - 2 * c1.r * deltaXToDeltaR * c1.cx
          - c1.cy ** 2 * deltaXToDeltaR ** 2
          + c1.cy ** 2 + x ** 2
          - 2 * x * c1.cx
          + c1.cx ** 2,
      );
      solutionA = (
        sqrtTerm
          + y * deltaYToDeltaR
          + deltaYToDeltaR ** 2 * c1.r
          - deltaYToDeltaR * c1.cy
          + c1.r * deltaXToDeltaR ** 2
          + x * deltaXToDeltaR
          - deltaXToDeltaR * c1.cx
      ) / (
        deltaYToDeltaR ** 2
          + deltaXToDeltaR ** 2
          - 1
      );
      solutionB = (
        -sqrtTerm
          + y * deltaYToDeltaR
          + deltaYToDeltaR ** 2 * c1.r
          - deltaYToDeltaR * c1.cy
          + c1.r * deltaXToDeltaR ** 2
          + x * deltaXToDeltaR
          - deltaXToDeltaR * c1.cx
      ) / (
        deltaYToDeltaR ** 2
          + deltaXToDeltaR ** 2
          - 1
      );
    }

    const solutionFactorA = (solutionA - c1.r) / (c2.r - c1.r);
    const solutionFactorB = (solutionB - c1.r) / (c2.r - c1.r);

    let factor;
    if ((solutionFactorA >= 0 && solutionFactorA <= 1)) {
      factor = solutionFactorA;
    } else if (solutionFactorB >= 0 && solutionFactorB <= 1) {
      factor = solutionFactorB;
    } else {
      throw new Error('No solutions to interpolation');
    }

    return factor * (c2.z - c1.z) + c1.z;
  }
}

function basicallyZero(v) {
  return v > -0.000001 && v < 0.000001;
}

exports.CircularContainer = CircularContainer;
